import { FormEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { useAdminDraft } from "@contexts/admin/AdminDraftsProvider";
import { caseStatusLabel } from "@pages/AdminDashboard/plainLanguageLabels";
import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { phoneSearchFormLayout } from "@pages/AdminDashboard/theme/phoneSearchForm";
import { Banner } from "@pages/AdminDashboard/ui/Banner";
import { Button } from "@pages/AdminDashboard/ui/Button";
import { DataTable, EmptyStateCard } from "@pages/AdminDashboard/ui/DataTable";
import { DateRange, isDateRangeInvalid } from "@pages/AdminDashboard/ui/DateRange";
import { Select } from "@pages/AdminDashboard/ui/Select";
import { TextField } from "@pages/AdminDashboard/ui/TextField";
import { getCases } from "@store/admin/adminAction";
import { CaseSearchState } from "@types-runway/admin";
import { Case, CaseStatuses } from "@types-runway/case";
import { RootState } from "@types-runway/state";

import { buildCaseColumns } from "./caseColumns";
import { CaseConditionsPanel } from "./CaseConditionsPanel";
import {
  buildCaseSearchParams,
  CaseSearchFilters,
  caseSearchPageSize,
  describeCaseSearchFilters,
  findDateRangeProblem,
  nextOffset,
  previousOffset,
} from "./caseSearchQuery";

const { color, radius, shadow, typeScale } = adminTokens;
const [, , spaceMd, spaceLg] = adminTokens.spacing;
const barPaddingY = 14;
const caseIdMinWidth = 200;
const statusFieldWidth = 150;

const Page = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spaceLg}px;
`;

const FilterBar = styled.form<{ $isPhone: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${spaceMd}px;
  box-sizing: border-box;
  padding: ${barPaddingY}px ${spaceLg}px;
  background-color: ${color.surface};
  border: 1px solid ${color.border};
  border-radius: ${radius.card}px;
  box-shadow: ${shadow.card};
  ${({ $isPhone }) => $isPhone && phoneSearchFormLayout}
`;

const CaseIdSlot = styled.div`
  flex: 1;
  min-width: ${caseIdMinWidth}px;
`;

const Count = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.meta.fontFamily};
  font-size: ${typeScale.meta.fontSize};
`;

const widenAdvice = "Widen the dates or clear the status.";
const emptyFilters: CaseSearchFilters = { caseId: "", status: "", from: "", to: "" };
// The design lists the statuses in this order.
const statusOrder = [
  CaseStatuses.CREATED,
  CaseStatuses.ASSIGNED,
  CaseStatuses.PROCESSING,
  CaseStatuses.WAITING,
  CaseStatuses.SUPPORT,
  CaseStatuses.COMPLETED,
  CaseStatuses.CANCELLED,
];
const statusOptions = [
  { value: "", label: "Any status" },
  ...statusOrder.map((status) => ({ value: status, label: caseStatusLabel(status) })),
];

const CaseSearch = () => {
  const isPhone = useIsPhoneLayout();
  const dispatch = useDispatch();
  const { status, rows, total, hasMore } = useSelector<RootState, CaseSearchState>(
    (state) => state.admin.caseSearch
  );

  const [filters, setFilters] = useAdminDraft("case.filters", emptyFilters);
  const [searchedFilters, setSearchedFilters] = useAdminDraft("case.searchedFilters", emptyFilters);
  const [offset, setOffset] = useAdminDraft("case.offset", 0);
  const [openConditionIds, setOpenConditionIds] = useAdminDraft<string[]>(
    "case.openConditionIds",
    []
  );

  const isPending = status === "pending";

  const runSearch = (searchFilters: CaseSearchFilters, searchOffset: number) => {
    setSearchedFilters(searchFilters);
    setOffset(searchOffset);
    dispatch(getCases(buildCaseSearchParams(searchFilters, searchOffset)));
  };

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isPending || findDateRangeProblem(filters)) return;

    runSearch(filters, 0);
  };

  const clearFilters = () => {
    setFilters(emptyFilters);
    runSearch(emptyFilters, 0);
  };

  const goToNextPage = () => runSearch(searchedFilters, nextOffset(offset));

  const goToPreviousPage = () => {
    if (offset === 0) return;

    runSearch(searchedFilters, previousOffset(offset));
  };

  const usedFilters = describeCaseSearchFilters(searchedFilters);
  const emptyMessage = usedFilters ? `${usedFilters}. ${widenAdvice}` : widenAdvice;

  const toggleConditions = (caseRecord: Case) => {
    const key = String(caseRecord.id);

    setOpenConditionIds((open) =>
      open.includes(key) ? open.filter((openKey) => openKey !== key) : [...open, key]
    );
  };

  const columns = buildCaseColumns({
    isConditionsOpen: ({ id }) => openConditionIds.includes(String(id)),
    onToggleConditions: toggleConditions,
  });

  return (
    <Page>
      <FilterBar $isPhone={isPhone} role="search" onSubmit={submitSearch}>
        <CaseIdSlot>
          <TextField
            id="admin-case-search-id"
            label="Telehealth case ID"
            placeholder="Exact match"
            value={filters.caseId}
            onChange={(event) => setFilters({ ...filters, caseId: event.target.value })}
          />
        </CaseIdSlot>
        <Select
          id="admin-case-search-status"
          label="Status"
          width={statusFieldWidth}
          options={statusOptions}
          value={filters.status}
          onChange={(event) => setFilters({ ...filters, status: event.target.value })}
        />
        <DateRange
          fromLabel="Created from"
          toLabel="Created to"
          from={filters.from}
          to={filters.to}
          invalidMessage={findDateRangeProblem(filters) ?? undefined}
          onChange={({ from, to }) => setFilters({ ...filters, from, to })}
        >
          <Button
            look="primary"
            type="submit"
            disabled={isPending || isDateRangeInvalid(filters.from, filters.to)}
            isPending={isPending}
            pendingLabel="Search"
          >
            Search
          </Button>
        </DateRange>
      </FilterBar>

      {status === "empty" && (
        <div data-testid="case-search-empty">
          <EmptyStateCard
            title="No cases match this search."
            message={emptyMessage}
            actionLabel="Clear filters"
            onAction={clearFilters}
          />
        </div>
      )}
      {status === "error" && (
        <div data-testid="case-search-error">
          <Banner tone="error" title="The case search failed. Try again." body="Nothing changed." />
        </div>
      )}

      {status === "found" && (
        <>
          <Count>{total === 1 ? "1 case" : `${total} cases`}</Count>
          <DataTable
            label="Cases"
            columns={columns}
            rows={rows}
            getRowKey={(caseRecord) => String(caseRecord.id)}
            getRowTestId={(caseRecord) => `case-row-${caseRecord.id}`}
            renderRowDetail={(caseRecord) =>
              openConditionIds.includes(String(caseRecord.id)) && (
                <CaseConditionsPanel caseRecord={caseRecord} />
              )
            }
            emptyState={{ title: "No cases match this search." }}
            pagination={{
              offset,
              pageSize: caseSearchPageSize,
              orderLabel: "newest first",
              previousLabel: "Previous page",
              nextLabel: "Next page",
              hasNextPage: hasMore,
              onPrevious: goToPreviousPage,
              onNext: goToNextPage,
            }}
          />
        </>
      )}
    </Page>
  );
};

export default CaseSearch;
